import React from "react";
import { Link } from "@tanstack/react-router";
import { Clock, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useDateFormat } from "../../hooks/useDateFormat";
import { portalArticleParam } from "../../hooks/useKnowledgeBase";
import { usePortalBranding } from "../../hooks/usePortalBranding";
import type { CustomerPortalArticleDto } from "../../types/articles";

interface PortalArticleCardProps {
  article: CustomerPortalArticleDto;
  orgSlug: string;
  // Hide the collection badge where the list is already one collection's
  showCollection?: boolean;
  // Called when the visitor opens the article (search results count it for the search insights)
  onOpen?: () => void;
}

/**
 * An article in a portal list (search results, a collection's page). The whole card is a link - reachable by
 * keyboard, openable in a new tab, and a real href for search engines - rather than a clickable div.
 */
export const PortalArticleCard: React.FC<PortalArticleCardProps> = ({ article, orgSlug, showCollection = true, onOpen }) => {
  const { formatDateTime } = useDateFormat();
  const { getThemedPrimaryColor, getThemedPrimaryWithOpacity } = usePortalBranding();
  const collectionLabel = article.collectionPath || article.collectionName;

  return (
    <Link
      to="/org/$orgSlug/portal/knowledge-base/$articleId"
      params={{ orgSlug, articleId: portalArticleParam(article) }}
      onClick={onOpen}
      className="group block rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{ "--tw-ring-color": getThemedPrimaryColor() } as React.CSSProperties}
    >
      <div className="space-y-2">
        {((showCollection && collectionLabel) || article.isFeatured) && (
          <div className="flex flex-wrap items-center gap-2">
            {showCollection && collectionLabel && (
              <Badge
                variant="outline"
                className="text-xs"
                style={{ borderColor: getThemedPrimaryWithOpacity(0.3), color: getThemedPrimaryColor() }}
              >
                {collectionLabel}
              </Badge>
            )}
            {article.isFeatured && (
              <Badge
                className="text-xs"
                style={{
                  backgroundColor: getThemedPrimaryWithOpacity(0.1),
                  color: getThemedPrimaryColor(),
                  border: `1px solid ${getThemedPrimaryWithOpacity(0.2)}`,
                }}
              >
                Featured
              </Badge>
            )}
          </div>
        )}
        <h4 className="font-semibold text-base sm:text-lg group-hover:underline" style={{ color: getThemedPrimaryColor() }}>
          {article.title}
        </h4>
        {article.summary && (
          <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm line-clamp-2">{article.summary}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center">
            <Eye className="mr-1 h-3 w-3" aria-hidden="true" />
            {article.viewCount?.toLocaleString() || "0"} views
          </span>
          <span className="flex items-center">
            <Clock className="mr-1 h-3 w-3" aria-hidden="true" />
            Updated {formatDateTime(article.updatedAt)}
          </span>
        </div>
      </div>
    </Link>
  );
};
